/* ══════════════════════════════════════════════════════════════════════════
 * WHICH PLACES THIS ELEATER HAS ALREADY SAVED
 *
 * ⚠️ WHY THIS FILE EXISTS. Every card in Eleat carries a heart, and until
 * 23 September 2026 the React card carried none at all. `10_Home_LOCKED.html`
 * draws `<svg class="heart">` on every single card in all four rails, and the
 * View All grids draw `.ghrt`. The React `VenueCard` drew the photograph, the
 * name and the meta line — and nothing else. Al saw it on his phone and said
 * so: *"even the heart is fucked."* It was not fucked. It was absent.
 *
 * ── ⚠️⚠️ THE HEART IS A CONTROL, EVERYWHERE. AL, 26 SEPTEMBER 2026 ──────────
 *
 * *"A person signs in and wants to heart a restaurant. If they're already
 * signed in, great, they can heart it. And at a later date, they can unheart
 * it. What's so fucking difficult about that? And same thing with the flag, to
 * save it and share it. What the fuck is so hard about that? They don't like
 * it, they can remove it."*
 *
 * **Tap it, it is saved. Tap it again, it is gone. On every card, on every
 * screen.** No exceptions, no screens where it only looks at you.
 *
 * ⚠️ THIS IS HIS CURRENT INSTRUCTION AND IT IS THE ONE THAT COUNTS. This file
 * used to carry a line of his from 28 August saying hearts should be tappable
 * only on the restaurant page, and I built the whole app around it — including
 * a `pointer-events: none` that made the heart deliberately dead on four
 * screens, and a gate that refused any attempt to change it. **When he asked
 * for Saved's buttons I scoped the exception to one screen instead of hearing
 * the simpler thing he was describing**, and then handed him "the heart is
 * tappable on Saved only" as if that were an answer. It reads as nonsense
 * because it was.
 *
 * ⚠️ THE OLD LINE IS GONE RATHER THAN KEPT BESIDE THE NEW ONE. CLAUDE.md
 * RULE 3: *"Never build a check, a row or a code path that accepts the old
 * behaviour as well."*  There is one behaviour now.
 *
 * ⚠️ AND THE PROTOTYPE'S OWN WARNING IS WHY THIS IS ONE MODULE. eleat_hearts
 * .js: *"SO THE KEY HAS TO MATCH ACROSS EVERY SURFACE. One restaurant, one
 * key, whether it was saved from its own page or is being recognised on a
 * card."* It had to slug the venue's NAME, because static HTML has no ids,
 * and a trailing-hyphen bug in that slug meant **every heart on Home read as
 * empty however much had been saved**. React has the real `id` on every row,
 * so there is no slug and no way for the two sides to disagree.
 *
 * ── ⚠️ ONE REQUEST, NOT ONE PER CARD ────────────────────────────────────
 *
 * Home draws up to 37 cards across four rails. `GET /restaurants/:id/saved`
 * once per card is 37 requests on a phone on Hong Kong mobile data to draw an
 * outline. `GET /restaurants/saved/me` answers the whole question in one, so
 * this fetches that once, holds it, and every card reads the answer.
 * ══════════════════════════════════════════════════════════════════════════ */

import { useEffect, useState } from 'react';
import { call, getToken, SIGNED_IN_EVENT } from './api.js';

/* ⚠️ MODULE LEVEL ON PURPOSE. Four rails mount at the same moment and each
 * would otherwise start its own fetch. The promise is shared, so the second
 * card through the door waits on the first card's request. */
let ids = new Set();
let asked = null;
const listeners = new Set();

function publish() {
  for (const fn of listeners) fn(ids);
}

function load() {
  if (asked) return asked;
  /* ⚠️ SIGNED OUT IS NOT AN EMPTY LIST, IT IS NO LIST — and the difference
   * only matters here because both draw the same thing: outline hearts. The
   * route is requireAuth, so asking without a token is a guaranteed 401 for
   * an answer we already know. */
  if (!getToken()) { asked = Promise.resolve(); return asked; }
  asked = call('/restaurants/saved/me', { auth: true })
    .then((d) => {
      /* ⚠️ THE ROUTE ANSWERS WITH A BARE ARRAY. `listSaved` ends `return
       * res.rows` — there is no `{ results }` wrapper, unlike discovery's
       * search. Read against restaurants.service.js, not guessed, and written
       * to accept either so a future wrapper cannot silently empty every
       * heart on every screen at once. Saved.jsx already does the same. */
      const rows = Array.isArray(d) ? d : ((d && d.results) || []);
      ids = new Set(rows.map((r) => r.id));
      publish();
    })
    /* ⚠️ A HEART IS NOT WORTH AN ERROR MESSAGE. If this fails, every card
     * shows an empty heart — which is what a new Eleater sees anyway — and
     * the screen it sits on carries on drawing venues. */
    .catch(() => {});
  return asked;
}

/* signed in on this page (no reload) — what was asked while signed out was
   "no list"; ask again for the real one */
if (typeof window !== 'undefined') {
  window.addEventListener(SIGNED_IN_EVENT, () => { asked = null; ids = new Set(); publish(); load(); });
}

/** The ids this Eleater has saved. Re-renders the caller when that changes. */
export function useSaved() {
  const [snapshot, setSnapshot] = useState(ids);
  useEffect(() => {
    const fn = (next) => setSnapshot(next);
    listeners.add(fn);
    load();
    return () => { listeners.delete(fn); };
  }, []);
  return snapshot;
}

/**
 * The venue page saved or unsaved something — tell every card at once.
 *
 * ⚠️ WITHOUT THIS THE HEART LIES UNTIL A RELOAD. An Eleater saves a place on
 * its own page, taps back to Home, and the card they just saved still shows
 * an empty heart. That is the drift this repository keeps paying for, in
 * miniature: two copies of one fact, and the one nobody is looking at is the
 * one that goes stale.
 */
/* the list as the server has it — the after-sign-in heart waits for it */
export function savedLoaded() { return load(); }

export function markSaved(id, on) {
  if (!id) return;
  const next = new Set(ids);
  if (on) next.add(id); else next.delete(id);
  ids = next;
  publish();
}
